import * as React from 'react';

import {RouteComponentProps} from 'react-router';
import AppEvent from '../../foundation/events/AppEvent';
import Lesson from '../modules/lessons/domain/aggregates/lesson/Lesson';
import EntityId from '../../foundation/domain/EntityId';
import Page from './Page';
import ExerciseApi from '../modules/lessons/api/ExerciseApi';
import LessonApi from '../modules/lessons/api/LessonApi';
import Exercise from '../modules/lessons/domain/aggregates/exercise/Exercise';
import ExerciseComponent from './components/exercises/ExerciseComponent';
import Registry from '../global/Registry';
import ExerciseResultApi from '../modules/lessons/api/ExerciseResultApi';
import ExerciseResult from '../modules/lessons/domain/aggregates/exerciseResult/ExerciseResult';
import Timepoint from '../../foundation/time/Timepoint';
import CourseApi from '../modules/lessons/api/CourseApi';
import ExerciseTemplateApi from '../modules/lessons/api/ExerciseTemplateApi';
import Course from '../modules/lessons/domain/aggregates/course/Course';
import * as _ from 'lodash';

interface Props {
	id: string;
	courseId: string;
}

interface State {
	lesson?: Lesson;
	course?: Course;
	exercises: Exercise[];
	exerciseResults: ExerciseResult[];
	currentExercise?: Exercise;
	loading: boolean;
	step: number;
}

export default class ExercisesPage extends Page<RouteComponentProps<Props>, State> {

	public constructor (props: RouteComponentProps<Props>) {
		super(props);

		this.state = { exercises: [], exerciseResults: [], loading: true, step: 0 };
	}

	async componentWillMount () {
		const lessonIdParameter = this.props.match.params.id;
		const userId = Registry.getInstance().me.getId();
		const loading = false;
		await ExerciseTemplateApi.getInstance().getAll();
		await CourseApi.getInstance().getAll();

		if (lessonIdParameter === undefined) {
			await LessonApi.getInstance().getAll();
			const exercises = await ExerciseApi.getInstance().getAllToRepeatByUserId(userId);
			const currentExercise = this.determineCurrentExercise(exercises);

			const courseIdParameter = this.props.match.params.courseId;
			const course = courseIdParameter === undefined ? undefined : Registry.getInstance().courseRepository.getById(new EntityId<Course>(Number(courseIdParameter)));

			this.setState({exercises, currentExercise, loading, course});

		} else {
			const lessonId = new EntityId<Lesson>(Number(lessonIdParameter));
			const lesson = await LessonApi.getInstance().getById(lessonId);
			const course = Registry.getInstance().courseRepository.getById(lesson.getCourseId());
			const exercises = await ExerciseApi.getInstance().getAllByLessonId(lessonId);
			const exerciseResults = await ExerciseResultApi.getInstance().getAllByUserIdAndLessonId(userId, lessonId);
			const currentExercise = this.determineCurrentExercise(exercises, exerciseResults);

			this.setState({lesson, exercises, exerciseResults, currentExercise, loading, course});
		}
	}

	public async processEvent (event: AppEvent) {
		return;
	}

	protected renderTitle () {
		return this.state.lesson !== undefined ? this.state.lesson.getTitle() + ' - Exercises' : 'Review Exercises - Brain Brooder';
	}

	protected renderDescription () {
		return '';
	}

	protected isPadded () {
		return false;
	}

	protected isLoading () {
		return this.state.loading;
	}

	protected shouldBeIndexed () {
		return false;
	}

	protected renderContent () {
		const { course, currentExercise, step } = this.state;

		return (
			<div>
				<div style={{width: this.determineProgress() + '%', height: '58px', backgroundColor: '#4183C4'}}/>
				<ExerciseComponent continueLink={course === undefined ? '/' : course.getSlug()} key={'exercise_' + (currentExercise === undefined ? 'undefined' : currentExercise.getId().getValue())} editingMode={false} onUpdate={this.update} onContinue={this.continue} exercise={currentExercise} step={step} />
			</div>
		);
	}

	private update = () => {
		this.setState({ exerciseResults: Registry.getInstance().exerciseResultRepository.getAllLastByExercises(this.state.exercises) });
	}

	private continue = () => {
		this.setState({ currentExercise: this.determineCurrentExercise(this.state.exercises, this.state.exerciseResults), step: this.state.step + 1 });
	}

	private determineExercisesLeft (exercises: Exercise[], exerciseResults: ExerciseResult[]) {
		return exercises.filter(exercise => !exerciseResults.some(result => result.getExerciseId().equals(exercise.getId()) && result.getNextTimepoint().isAfter(Timepoint.now()) && !result.isLevel0()));
	}

	private determineCurrentExercise (exercises: Exercise[], exerciseResults: ExerciseResult[] = []) {
		const exercisesLeft = this.determineExercisesLeft(exercises, exerciseResults);

		// If there are multiple due exercises, just return the first one, this maintains the random order of the exercises to be repeated
		const dueExercise = exercisesLeft.find(exercise => !exerciseResults.some(result => result.getExerciseId().equals(exercise.getId()) && result.getNextTimepoint().isAfter(Timepoint.now())));

		if (dueExercise !== undefined) {
			return dueExercise;
		}

		// If no exercise is past the due date, return the one that was repeated longest ago (these are level 0 exercises that all have due dates within 30 seconds)
		return _.minBy(exercisesLeft, exercise => (exerciseResults.find(result => result.getExerciseId().equals(exercise.getId())) as ExerciseResult).getNextTimepoint().getMicrotimestamp());
	}

	private determineProgress () {
		const exercises = this.state.exercises;
		const exerciseResults = this.state.exerciseResults;

		const numberOfFinishedExercises = exerciseResults.filter(result => !result.isLevel0() && result.getNextTimepoint().isAfter(Timepoint.now())).length;

		return numberOfFinishedExercises * 100 / exercises.length;
	}
}


// WEBPACK FOOTER //
// ./code/project/pages/ExercisesPage.tsx